import { useEffect, useMemo, useState } from 'react';
import { isLiability, outstandingPaise, type Account } from '../../core/accounts';
import { addMonths, formatDateKey, formatMonthKey, monthKeyOf, todayKey } from '../../core/dates';
import type { Instalment, Loan } from '../../core/loans';
import { formatPaise, parseAmount } from '../../core/money';
import {
  cardOccurrences,
  dueNow,
  isOverdue,
  loanOccurrences,
  occurrencesIn,
  project,
  projectionTotals,
  type Occurrence,
  type RecurringItem,
} from '../../core/recurring';
import { watchAccounts } from '../../data/accounts';
import type { UserRecord } from '../../data/auth';
import { watchLoans, watchSchedule } from '../../data/loans';
import { withDefaults } from '../../data/preferences';
import {
  dismissOccurrence,
  handledKeys,
  postOccurrence,
  undismiss,
  watchHandled,
  watchRecurring,
  type HandledOccurrence,
} from '../../data/recurring';

interface Props {
  record: UserRecord;
}

const PROJECTION_MONTHS = 12;

/**
 * Due now, and the 12-month projection beside it (FR-REC-02, FR-REC-05).
 *
 * Two views of the same occurrences: what needs confirming, and where the next year goes
 * if nothing changes. The workbook kept these as separate blocks (P:R and T:AF) that had
 * to be reconciled by hand.
 */
export function DueScreen({ record }: Props) {
  const today = todayKey();
  const prefs = withDefaults(record.preferences);

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [items, setItems] = useState<RecurringItem[]>([]);
  const [handled, setHandled] = useState<Map<string, HandledOccurrence>>(new Map());
  const [loans, setLoans] = useState<Loan[]>([]);
  const [schedule, setSchedule] = useState<Instalment[]>([]);

  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [dates, setDates] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showProjection, setShowProjection] = useState(false);

  useEffect(() => watchAccounts(setAccounts), []);
  useEffect(() => watchRecurring(setItems), []);
  useEffect(() => watchHandled(setHandled), []);
  useEffect(() => watchLoans(setLoans), []);
  useEffect(() => {
    if (loans.length === 0) return;
    return watchSchedule(loans[0].id, setSchedule);
  }, [loans[0]?.id]);

  const accountType = useMemo(() => {
    const map = new Map(accounts.map((a) => [a.id, a.type]));
    return (id: string) => map.get(id);
  }, [accounts]);

  const nameOf = (id: string | null) =>
    id ? (accounts.find((a) => a.id === id)?.name ?? id) : '—';

  const months = useMemo(
    () => Array.from({ length: PROJECTION_MONTHS }, (_, i) => addMonths(monthKeyOf(today), i)),
    [today],
  );

  /** FR-REC-07: cards and loans supply their own, from their own modules. */
  const external = useMemo(() => {
    const cards = accounts
      .filter((a) => a.type === 'credit_card' && !a.archived && isLiability(a.type))
      .map((a) => ({
        accountId: a.id,
        name: a.name,
        outstandingPaise: outstandingPaise(a.balancePaise),
        dueDay: a.dueDay ?? 1,
        paymentAccountId: prefs.defaultAccountId ?? null,
      }));

    const emis = loans.flatMap((loan) => {
      const unpaid = schedule.filter((i) => i.dueDateKey >= today);
      return unpaid.slice(0, PROJECTION_MONTHS).map((instalment) => ({
        loanId: loan.id,
        accountId: loan.accountId,
        name: loan.name,
        dueDateKey: instalment.dueDateKey,
        amountPaise: instalment.amountPaise,
        paymentAccountId: prefs.defaultAccountId ?? null,
      }));
    });

    return [
      ...months.flatMap((monthKey) => cardOccurrences(cards, monthKey)),
      ...loanOccurrences(emis),
    ];
  }, [accounts, loans, schedule, months, today, prefs.defaultAccountId]);

  const hidden = useMemo(() => handledKeys(handled), [handled]);

  const due = useMemo(() => {
    const fromItems = months.flatMap((monthKey) => occurrencesIn(items, monthKey));
    return dueNow([...fromItems, ...external], today, { handled: hidden });
  }, [items, external, months, today, hidden]);

  const projection = useMemo(() => project(items, months, external), [items, months, external]);
  const totals = projectionTotals(projection);

  const dismissed = useMemo(
    () => [...handled.values()].filter((h) => h.outcome === 'dismissed'),
    [handled],
  );

  function amountFor(occurrence: Occurrence): string {
    return amounts[occurrence.key] ?? (occurrence.amountPaise / 100).toFixed(2);
  }

  async function post(occurrence: Occurrence) {
    setError(null);
    const paise = parseAmount(amountFor(occurrence));
    if (paise === null || paise <= 0) {
      setError('Enter an amount to post.');
      return;
    }

    setBusy(occurrence.key);
    try {
      await postOccurrence(
        occurrence,
        paise,
        dates[occurrence.key] ?? occurrence.dueDateKey,
        record.uid,
        accountType,
      );
      // The estimate on the item is deliberately untouched (FR-REC-03).
      setNotice(
        paise === occurrence.amountPaise
          ? `Posted ${occurrence.description}.`
          : `Posted ${occurrence.description} at ${formatPaise(paise)} against an estimate of ${formatPaise(occurrence.amountPaise)}. The estimate is unchanged.`,
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not post that.');
    } finally {
      setBusy(null);
    }
  }

  async function dismiss(occurrence: Occurrence) {
    setError(null);
    setBusy(occurrence.key);
    try {
      await dismissOccurrence(occurrence, today);
      setNotice(`Dismissed ${occurrence.description} for ${formatMonthKey(occurrence.monthKey)}.`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not dismiss that.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div className="head-row">
        <h1>Due now</h1>
        <button type="button" className="btn btn-quiet" onClick={() => setShowProjection((v) => !v)}>
          {showProjection ? 'Hide the projection' : 'Show the 12-month projection'}
        </button>
      </div>

      {error && <p className="error">{error}</p>}
      {notice && <p className="ok">{notice}</p>}

      {items.length === 0 && external.length === 0 ? (
        <div className="placeholder">
          <p>
            No recurring items yet. <strong>RECURRING-DRAFT.md</strong> holds what the
            workbook's commitments block and forecast contain — edit it, and the seeder
            writes what it says.
          </p>
          <p className="muted tiny">
            Card bills and loan EMIs appear here on their own once those accounts carry a
            balance (FR-REC-07); nothing needs defining by hand for them.
          </p>
        </div>
      ) : due.length === 0 ? (
        <div className="placeholder">Nothing due in the next week.</div>
      ) : (
        <div className="table-scroll">
          <table className="table reclassify-table">
            <thead>
              <tr>
                <th>Due</th>
                <th>Item</th>
                <th>From</th>
                <th className="num">Estimate</th>
                <th className="num">Post</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {due.map((occurrence) => (
                <tr key={occurrence.key}>
                  <td>
                    {formatDateKey(occurrence.dueDateKey, 'dd MMM')}
                    {isOverdue(occurrence, today) && <span className="badge warn-badge">overdue</span>}
                  </td>
                  <td>
                    {occurrence.description}
                    {occurrence.source !== 'recurring' && (
                      <span className="badge">{occurrence.source}</span>
                    )}
                  </td>
                  <td>
                    {occurrence.type === 'transfer'
                      ? `${nameOf(occurrence.accountId)} → ${nameOf(occurrence.toAccountId)}`
                      : nameOf(occurrence.accountId)}
                  </td>
                  <td className="num muted">
                    {formatPaise(occurrence.amountPaise, { symbol: false })}
                  </td>
                  <td className="num">
                    {/* FR-REC-03, the central requirement: the amount is editable before
                        posting. Pre-filled with the estimate, and changing it here never
                        changes the estimate. */}
                    <input
                      type="text"
                      inputMode="decimal"
                      className="amount-cell"
                      value={amountFor(occurrence)}
                      onChange={(e) =>
                        setAmounts((a) => ({ ...a, [occurrence.key]: e.target.value }))
                      }
                      aria-label={`Amount for ${occurrence.description}`}
                    />
                  </td>
                  <td className="actions">
                    <input
                      type="date"
                      value={dates[occurrence.key] ?? occurrence.dueDateKey}
                      onChange={(e) => setDates((d) => ({ ...d, [occurrence.key]: e.target.value }))}
                      aria-label={`Date for ${occurrence.description}`}
                    />
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={busy !== null}
                      onClick={() => void post(occurrence)}
                    >
                      {busy === occurrence.key ? '…' : 'Post'}
                    </button>
                    <button
                      type="button"
                      className="btn-link"
                      disabled={busy !== null}
                      onClick={() => void dismiss(occurrence)}
                    >
                      Skip
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dismissed.length > 0 && (
        <p className="muted tiny">
          {dismissed.length} skipped.{' '}
          <button
            type="button"
            className="btn-link"
            onClick={() => void undismiss(dismissed[dismissed.length - 1], today).catch((c) => setError(String(c)))}
          >
            Put the last one back
          </button>
        </p>
      )}

      {showProjection && (
        <>
          <div className="head-row" style={{ marginTop: '1.25rem' }}>
            <h2>Next 12 months</h2>
            <span className="muted tiny">
              income {formatPaise(totals.incomePaise)} · expenses {formatPaise(totals.expensePaise)} ·
              net {formatPaise(totals.netPaise)}
            </span>
          </div>

          {totals.incomePaise === 0 && (
            // The workbook's income rows are blank; saying so beats showing a deficit
            // that is an artefact rather than a fact.
            <p className="warn">
              No recurring income defined yet, so the net below is outflow only. The income
              heads are listed in RECURRING-DRAFT.md awaiting figures.
            </p>
          )}

          <div className="table-scroll">
            <table className="table reclassify-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th className="num">Income</th>
                  <th className="num">Expenses</th>
                  <th className="num">Transfers</th>
                  <th className="num">Net</th>
                  <th className="num">Items</th>
                </tr>
              </thead>
              <tbody>
                {projection.map((month) => (
                  <tr key={month.monthKey}>
                    <td>{formatMonthKey(month.monthKey)}</td>
                    <td className="num">{formatPaise(month.incomePaise, { symbol: false })}</td>
                    <td className="num">{formatPaise(month.expensePaise, { symbol: false })}</td>
                    <td className="num muted">{formatPaise(month.transferPaise, { symbol: false })}</td>
                    <td className={`num ${month.netPaise < 0 ? 'negative' : ''}`}>
                      {formatPaise(month.netPaise, { symbol: false })}
                    </td>
                    <td className="num muted">{month.occurrences.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="muted tiny">
            Each month is summed from its own items, so a bonus in one month raises that
            month alone. The workbook used an absolute <code>SUM($U$37:$U$44)</code> and so
            showed the same income in all twelve columns — defect 4, not reproduced.
          </p>
        </>
      )}
    </>
  );
}
